import React, { useState } from 'react';
import './StepsPickerModal.css';

/**
 * Props for StepsPickerModal component
 * 
 * Requirements: 3.6, 15.4, 15.5, 15.6, 15.7
 */
export interface StepsPickerModalProps {
  /** Current Steps value */
  currentSteps: number;
  /** Callback when Steps is changed and confirmed */
  onStepsChange: (steps: number) => void;
  /** Callback when modal is closed */
  onClose: () => void;
}

/**
 * StepsPickerModal Component
 * 
 * Centered modal for changing Steps value.
 * Displays as centered modal with z-index: 101.
 * Shows steps selector with options: 8, 16, 32, 64.
 * Provides Cancel and Confirm buttons (44x44px minimum).
 * 
 * Requirements: 3.6, 15.4, 15.5, 15.6, 15.7
 */
export const StepsPickerModal: React.FC<StepsPickerModalProps> = ({
  currentSteps,
  onStepsChange,
  onClose,
}) => {
  // Local state for Steps value before confirmation
  const [steps, setSteps] = useState<number>(currentSteps);
  const [customSteps, setCustomSteps] = useState<string>(currentSteps.toString());
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Steps options - Common values
  const STEPS_OPTIONS = [8, 16, 32, 64, 128];

  /**
   * Handle steps option selection
   * Allow common values: 8, 16, 32, 64, 128
   */
  const handleStepsSelect = (value: number) => {
    setSteps(value);
    setCustomSteps(value.toString());
    setShowCustomInput(false);
  };

  /**
   * Handle custom steps input
   */
  const handleCustomStepsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomSteps(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 4) {
      setSteps(numValue);
    }
  };

  /**
   * Toggle custom input
   */
  const handleCustomClick = () => {
    setShowCustomInput(true);
  };

  /**
   * Handle confirm button click
   * Requirement 15.6: Update composition on confirmation
   */
  const handleConfirm = () => {
    onStepsChange(steps);
    onClose();
  };

  /**
   * Handle cancel button click
   * Requirement 15.7: Provide cancel button
   */
  const handleCancel = () => {
    onClose();
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = () => {
    onClose();
  };

  /**
   * Prevent modal content clicks from closing the modal
   */
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      {/* Backdrop overlay - Requirement 15.4: Centered modal */}
      <div 
        className="steps-picker-backdrop" 
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Modal container - Requirement 15.4: z-index: 101 */}
      <div 
        className="steps-picker-modal" 
        onClick={handleModalClick}
        onKeyDown={handleKeyDown}
        role="dialog" 
        aria-label="Steps picker"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="steps-picker-header">
          <h3 className="steps-picker-title">Set Steps</h3>
          <p className="steps-picker-subtitle">
            Choose pattern length
          </p>
        </div>

        {/* Modal Content - Requirement 15.5: Steps selector with options */}
        <div className="steps-picker-content">
          {/* Steps Display */}
          <div className="steps-display">
            <div className="steps-value">{steps}</div>
            <div className="steps-label">STEPS</div>
          </div>

          {/* Steps Options - Common values: 8, 16, 32, 64, 128 */}
          <div className="steps-options">
            {STEPS_OPTIONS.map((option) => (
              <button
                key={option}
                className={`steps-option ${steps === option ? 'active' : ''}`}
                onClick={() => handleStepsSelect(option)}
                aria-label={`Select ${option} steps`}
                aria-pressed={steps === option}
                title={`${option} steps`}
              >
                {option}
              </button>
            ))}
            <button
              className={`steps-option ${showCustomInput ? 'active' : ''}`}
              onClick={handleCustomClick}
              aria-label="Enter custom steps"
              title="Custom steps"
            >
              Custom
            </button>
          </div>

          {/* Custom Steps Input */}
          {showCustomInput && (
            <div className="steps-custom-input">
              <input
                type="number"
                min="4"
                value={customSteps}
                onChange={handleCustomStepsChange}
                placeholder="Enter steps (min 4)"
                aria-label="Custom steps value"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Modal Footer - Requirements 15.6, 15.7: Cancel and Confirm buttons */}
        <div className="steps-picker-footer">
          <button
            className="steps-picker-btn steps-picker-cancel"
            onClick={handleCancel}
            aria-label="Cancel steps change"
            title="Cancel"
          >
            Cancel
          </button>
          <button
            className="steps-picker-btn steps-picker-confirm"
            onClick={handleConfirm}
            aria-label="Confirm steps change"
            title="Confirm"
          >
            Confirm
          </button>
        </div>
      </div>
    </>
  );
};
